import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthContextType, LoginCredentials, User, Permission, UserRole } from '../types/auth';
import { AuthAPI, TokenStorage } from '../lib/auth';

const AuthContext = createContext<AuthContextType | null>(null);

const getTokenRemainingSeconds = (token: string | null): number | null => {
    if (!token) return null;

    try {
        const [, payloadPart] = token.split('.');
        if (!payloadPart) return null;

        const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized.padEnd(
            normalized.length + ((4 - (normalized.length % 4)) % 4),
            '='
        );
        const payload = JSON.parse(atob(padded)) as { exp?: number };
        if (!payload.exp) return null;

        const nowInSeconds = Math.floor(Date.now() / 1000);
        const remaining = payload.exp - nowInSeconds;
        return remaining > 0 ? remaining : null;
    } catch (error) {
        console.error('Failed to decode access token payload', error);
        return null;
    }
};

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
    const queryClient = useQueryClient();

    // 快取的 queryKey 不含租戶與使用者，換帳號或登出時必須整批丟棄，否則會看到前一位的資料
    const clearSession = () => {
        TokenStorage.clearTokens();
        TokenStorage.clearUser();
        queryClient.clear();
        setUser(null);
    };

    // 設置自動刷新 Token 的計時器
    const setupAutoRefresh = (expiresIn: number) => {
        // 清除現有計時器
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
        }

        // 在 Token 過期前 5 分鐘刷新
        const refreshTime = Math.max((expiresIn - 300) * 1000, 60000); // 至少等待 1 分鐘

        refreshTimerRef.current = setTimeout(async () => {
            try {
                await refreshAuth();
            } catch (error) {
                console.error('Auto refresh failed:', error);
                // 刷新失敗，清除登入狀態
                clearSession();
            }
        }, refreshTime);
    };

    // 初始化：檢查是否有已登入的 session
    useEffect(() => {
        const initAuth = async () => {
            const accessToken = TokenStorage.getAccessToken();
            const storedUser = TokenStorage.getUser();

            if (accessToken && storedUser) {
                try {
                    // 驗證 token 是否有效
                    const user = await AuthAPI.verify();
                    setUser(user);

                    const remainingSeconds = getTokenRemainingSeconds(accessToken) ?? 3600;
                    setupAutoRefresh(remainingSeconds);
                } catch (error) {
                    // Token 無效，嘗試刷新
                    try {
                        await refreshAuth();
                    } catch (refreshError) {
                        // 刷新也失敗，清除
                        clearSession();
                    }
                }
            }
            setIsLoading(false);
        };

        initAuth();

        // 清理計時器
        return () => {
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
            }
        };
    }, []);

    // 登入
    const login = async (credentials: LoginCredentials) => {
        setIsLoading(true);
        try {
            const response = await AuthAPI.login(credentials);

            queryClient.clear();

            // 儲存 tokens 和 user
            TokenStorage.setAccessToken(response.accessToken);
            TokenStorage.setRefreshToken(response.refreshToken);
            TokenStorage.setUser(response.user);
            TokenStorage.setTenantId(response.user.tenantId);

            setUser(response.user);

            // 設置自動刷新
            setupAutoRefresh(response.expiresIn);
        } finally {
            setIsLoading(false);
        }
    };

    // 登出
    const logout = async () => {
        setIsLoading(true);
        try {
            // 清除自動刷新計時器
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
            }

            await AuthAPI.logout();
        } finally {
            clearSession();
            setIsLoading(false);
        }
    };

    // 刷新認證
    const refreshAuth = async () => {
        const refreshToken = TokenStorage.getRefreshToken();
        if (!refreshToken) throw new Error('No refresh token');

        const response = await AuthAPI.refresh(refreshToken);

        TokenStorage.setAccessToken(response.accessToken);
        TokenStorage.setRefreshToken(response.refreshToken);
        TokenStorage.setUser(response.user);
        TokenStorage.setTenantId(response.user.tenantId);

        setUser(response.user);

        // 重新設置自動刷新
        setupAutoRefresh(response.expiresIn);
    };

    // 檢查權限（支援分組）
    const hasPermission = (permission: Permission): boolean => {
        if (!user) return false;

        // 直接匹配
        if (user.permissions.includes(permission)) {
            return true;
        }

        // 檢查父級權限
        // 例如：檢查 'members:read'，如果有 'members' 權限也視為有權限
        const parts = permission.split(':');
        for (let i = parts.length - 1; i > 0; i--) {
            const parentPermission = parts.slice(0, i).join(':') as Permission;
            if (user.permissions.includes(parentPermission)) {
                return true;
            }
        }

        return false;
    };

    // 檢查角色
    const hasRole = (role: UserRole | UserRole[]): boolean => {
        if (!user) return false;
        const roles = Array.isArray(role) ? role : [role];
        return roles.includes(user.role);
    };

    const value: AuthContextType = {
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        refreshAuth,
        hasPermission,
        hasRole,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
